import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import { authService, profileService } from '../../../services/supabaseService';

const RegistrationForm = ({ selectedGender, onSubmit, onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    confirmPassword: '',
    ageConfirmed: false
  });

  const [errors, setErrors] = useState({});
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));

    if (field === 'password') {
      calculatePasswordStrength(value);
    }
  };

  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password?.length >= 8) strength += 25;
    if (password?.match(/[a-z]/) && password?.match(/[A-Z]/)) strength += 25;
    if (password?.match(/[0-9]/)) strength += 25;
    if (password?.match(/[^a-zA-Z0-9]/)) strength += 25;
    setPasswordStrength(strength);
  };

  const checkUsernameAvailability = async () => {
    if (formData?.username?.length < 3) return;
    
    setUsernameChecking(true);
    try {
      const isAvailable = await profileService?.checkUsernameAvailability(formData?.username);
      setUsernameAvailable(isAvailable);
    } catch (error) {
      console.error('Username check error:', error);
      setUsernameAvailable(null);
    } finally {
      setUsernameChecking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData?.username) {
      newErrors.username = 'Username is required';
    } else if (formData?.username?.length < 3) {
      newErrors.username = 'Username must be at least 3 characters';
    } else if (usernameAvailable === false) {
      newErrors.username = 'Username is already taken';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.ageConfirmed) {
      newErrors.ageConfirmed = 'You must confirm you are 18 or older';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      const email = formData?.email;
      
      // Prepare profile data for raw_user_meta_data
      const profileData = {
        username: formData?.username,
        display_name: formData?.username,
        gender: selectedGender,
        age: 25, // Default age, will be updated in profile creation
        city: 'Unknown', // Will be updated in profile creation
        relationship_status: 'single',
        looking_for: '',
        bio: '',
        interests: []
      };

      const { data, error } = await authService?.signUp(email, formData?.password, profileData);

      if (error) {
        // Handle rate limit errors with helpful message
        if (error?.status === 429 || error?.code === '429' || error?.message?.includes('rate limit') || error?.message?.includes('Too Many Requests') || error?.message?.includes('wait') || error?.message?.includes('rate limit reached')) {
          setErrors({ 
            email: 'Registration rate limit reached. This is a temporary Supabase restriction (typically 3 signups/hour). Please wait 1-2 hours, use demo accounts for testing, or contact your administrator to check Supabase project settings.' 
          });
        } else if (error?.message?.includes('already registered') || error?.message?.includes('User already registered')) {
          setErrors({ email: 'This email is already registered. Please sign in instead.' });
        } else {
          setErrors({ email: error?.message || 'Registration failed. Please try again.' });
        }
      } else {
        // Success - pass data to parent for OTP verification
        onSubmit({ ...formData, email, userId: data?.user?.id });
      }
    } catch (error) {
      // Handle unexpected errors
      let errorMessage = 'An error occurred. Please try again.';
      if (error?.status === 429 || error?.code === '429') {
        errorMessage = 'Too many registration attempts. Please wait 5-10 minutes before trying again.';
      }
      setErrors({ email: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength <= 25) return 'bg-error';
    if (passwordStrength <= 50) return 'bg-warning';
    if (passwordStrength <= 75) return 'bg-primary';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength <= 25) return 'Weak';
    if (passwordStrength <= 50) return 'Fair';
    if (passwordStrength <= 75) return 'Good';
    return 'Strong';
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="email"
        label="Email Address"
        placeholder="Enter your email"
        value={formData?.email}
        onChange={(e) => handleInputChange('email', e?.target?.value)}
        error={errors?.email}
        required
      />
      <div className="relative">
        <Input
          type="text"
          label="Username"
          placeholder="Choose a unique username"
          value={formData?.username}
          onChange={(e) => handleInputChange('username', e?.target?.value)}
          onBlur={checkUsernameAvailability}
          error={errors?.username}
          description="Minimum 3 characters, will be visible to others"
          required
        />
        {usernameChecking && (
          <div className="absolute right-3 top-9">
            <Icon name="Loader" size={16} className="animate-spin text-primary" />
          </div>
        )}
        {usernameAvailable === true && (
          <div className="absolute right-3 top-9">
            <Icon name="CheckCircle" size={16} color="var(--color-success)" />
          </div>
        )}
        {usernameAvailable === false && (
          <div className="absolute right-3 top-9">
            <Icon name="XCircle" size={16} color="var(--color-error)" />
          </div>
        )}
      </div>
      <div className="relative">
        <Input
          type={showPassword ? 'text' : 'password'}
          label="Password"
          placeholder="Create a strong password"
          value={formData?.password}
          onChange={(e) => handleInputChange('password', e?.target?.value)}
          error={errors?.password}
          required
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
        >
          <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
        </button>
        {formData?.password && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Password strength:</span>
              <span className={`text-xs font-medium ${
                passwordStrength <= 25 ? 'text-error' :
                passwordStrength <= 50 ? 'text-warning' :
                passwordStrength <= 75 ? 'text-primary' : 'text-success'
              }`}>
                {getPasswordStrengthText()}
              </span>
            </div>
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full transition-all ${getPasswordStrengthColor()}`}
                style={{ width: `${passwordStrength}%` }}
              />
            </div>
          </div>
        )}
      </div>
      <div className="relative">
        <Input
          type={showConfirmPassword ? 'text' : 'password'}
          label="Confirm Password"
          placeholder="Re-enter your password"
          value={formData?.confirmPassword}
          onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
          error={errors?.confirmPassword}
          required
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-smooth"
        >
          <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={16} />
        </button>
      </div>
      <div className={`p-4 rounded-lg ${errors?.ageConfirmed ? 'bg-error/10 border border-error' : 'bg-muted'}`}>
        <Checkbox
          label="I confirm that I am 18 years or older"
          description="You must be an adult to use this platform"
          checked={formData?.ageConfirmed}
          onChange={(e) => handleInputChange('ageConfirmed', e?.target?.checked)}
          error={errors?.ageConfirmed}
          required
        />
      </div>
      <Button
        type="submit"
        variant="default"
        fullWidth
        iconName="UserPlus"
        iconPosition="left"
        disabled={isLoading || usernameChecking || usernameAvailable === false}
      >
        {isLoading ? 'Creating Account...' : 'Create Account'}
      </Button>
      
      {errors?.email && (errors?.email?.includes('rate limit') || errors?.email?.includes('429')) && (
        <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={16} className="flex-shrink-0 mt-0.5 text-warning" />
            <p className="text-xs text-muted-foreground">
              Too many sign-up attempts. Please try again later.
            </p>
          </div>
        </div>
      )}

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToLogin}
          className="text-sm text-primary hover:underline font-medium"
        >
          Already have an account? Sign in
        </button>
      </div>
    </form>
  );
};

export default RegistrationForm;